package org.moxultimate.topwithmox.components.searchbar

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Input
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.MaxDescriptionFontSize
import org.moxultimate.topwithmox.theme.MinDescriptionFontSize
import org.moxultimate.topwithmox.theme.MobileMinDescriptionFontSize

/**
 * SearchBarWithAutocomplete
 * Controlled search bar that shows a filtered dropdown as the user types.
 * Supports keyboard navigation (ArrowDown/Up to highlight, Enter to confirm).
 * Clicking a suggestion, pressing Enter or clicking the search icon fires [onSearch] with the chosen value.
 *
 * @param value Current text value (controlled — managed by the caller).
 * @param onValueChange Called on every keystroke so the caller can update [value].
 * @param suggestions Full list of autocomplete candidates (tag strings from the API).
 * @param modifier External modifier applied to the inner search row.
 * @param placeholder Placeholder text shown when the field is empty.
 * @param onSearch Called when the user confirms a query (Enter key, search icon or suggestion click).
 */
@Composable
fun SearchBarWithAutocomplete(
    value: String,
    onValueChange: (String) -> Unit,
    suggestions: List<String>,
    modifier: Modifier = Modifier,
    placeholder: String = "Search...",
    onSearch: (String) -> Unit,
) {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM

    val barHeight = if (isMobile) 48.px else 56.px
    val barPadding = if (isMobile) 16.px else 24.px
    val barRadius = if (isMobile) 20.px else 24.px
    val iconMargin = if (isMobile) 8.px else 12.px
    val dropdownItemPadding = if (isMobile) "8px 16px" else "12px 24px"
    val dropdownFontSize = if (isMobile) "13px" else "14px"

    var showDropdown by remember { mutableStateOf(false) }
    var selectedIndex by remember { mutableStateOf(-1) }
    var hoveredIndex by remember { mutableStateOf(-1) }

    val filtered = remember(value, suggestions) {
        if (value.isBlank()) emptyList()
        else suggestions.filter { it.contains(value, ignoreCase = true) }.take(8)
    }

    // Reset keyboard selection when typed value changes externally (e.g. chip sets it)
    LaunchedEffect(value) {
        selectedIndex = -1
        if (value.isBlank()) showDropdown = false
    }

    // Outer container must be position:relative so the absolute dropdown anchors to it
    Box(modifier = Modifier
        .fillMaxWidth()
        .position(Position.Relative)) {

        // Search bar row — mirrors SearchBar.kt visually
        Row(
            modifier = modifier
                .fillMaxWidth()
                .height(barHeight)
                .padding(leftRight = barPadding)
                .backgroundColor(Colors.White)
                .borderRadius(barRadius)
                .boxShadow(blurRadius = 4.px, color = Colors.LightGray),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SearchIcon(
                modifier = Modifier
                    .margin(right = iconMargin)
                    .color(Colors.Gray)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        showDropdown = false
                        selectedIndex = -1
                        if (value.isNotBlank()) onSearch(value)
                    },
            )
            Input(
                type = InputType.Search,
                value = value,
                onValueChange = {
                    onValueChange(it)
                    showDropdown = it.isNotBlank()
                },
                placeholder = placeholder,
                modifier = Modifier
                    .fillMaxSize()
                    .backgroundColor(Colors.Transparent)
                    .border(width = 0.px, color = Colors.Transparent)
                    .outline(width = 0.px, style = LineStyle.None, color = Colors.Transparent)
                    .boxShadow(blurRadius = 0.px, color = Colors.Transparent)
                    .fontFamily(FontFamily)
                    .fontSize(
                        if (isMobile) MobileMinDescriptionFontSize
                        else clamp(min = MinDescriptionFontSize, value = 1.vw, max = MaxDescriptionFontSize)
                    )
                    .fontWeight(FontWeight.Normal)
                    .onKeyDown { event ->
                        when (event.key) {
                            "ArrowDown" -> {
                                if (filtered.isNotEmpty()) {
                                    showDropdown = true
                                    selectedIndex = (selectedIndex + 1).coerceAtMost(filtered.lastIndex)
                                }
                            }
                            "ArrowUp" -> {
                                if (filtered.isNotEmpty()) {
                                    selectedIndex = (selectedIndex - 1).coerceAtLeast(0)
                                }
                            }
                            "Enter" -> {
                                val chosen = if (selectedIndex in filtered.indices) filtered[selectedIndex] else value
                                showDropdown = false
                                selectedIndex = -1
                                if (chosen.isNotBlank()) {
                                    onValueChange(chosen)
                                    onSearch(chosen)
                                }
                            }
                            "Escape" -> {
                                showDropdown = false
                                selectedIndex = -1
                            }
                        }
                    },
            )
        }

        // Autocomplete dropdown — absolutely positioned below the search row
        if (showDropdown && filtered.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "62px")
                    property("left", "0")
                    property("right", "0")
                    property("background-color", "#ffffff")
                    property("border", "1px solid #e8e8e8")
                    property("border-radius", "16px")
                    property("box-shadow", "0 4px 16px rgba(0,0,0,0.08)")
                    property("z-index", "999")
                    property("max-height", "280px")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                }
            }) {
                filtered.forEachIndexed { index, suggestion ->
                    // Highlighted when keyboard-navigated or mouse-hovered
                    val isHighlighted = index == selectedIndex || index == hoveredIndex
                    Div(attrs = {
                        onMouseOver { hoveredIndex = index }
                        onMouseOut { if (hoveredIndex == index) hoveredIndex = -1 }
                        onClick {
                            showDropdown = false
                            selectedIndex = -1
                            hoveredIndex = -1
                            onValueChange(suggestion)
                            onSearch(suggestion)
                        }
                        style {
                            property("padding", dropdownItemPadding)
                            property("cursor", "pointer")
                            property("background-color", if (isHighlighted) "#f5f5f5" else "#ffffff")
                            property("font-family", "'Kannada MN', sans-serif")
                            property("font-size", dropdownFontSize)
                            property("color", "#333333")
                            if (index < filtered.lastIndex) {
                                property("border-bottom", "1px solid #f5f5f5")
                            }
                            property("transition", "background-color 0.15s ease")
                        }
                    }) {
                        Text(suggestion)
                    }
                }
            }
        }
    }
}
